import { useRef, useState, useEffect } from "react";
import type { FormEvent, ChangeEvent } from "react";
import { useSearch } from "./SearchContext";
import ReactDOM from "react-dom";
import SearchResults from "./SearchResults";
import { useTranslation } from "react-i18next";
import "./SearchButton.css";

interface SearchButtonProps {
  onSearch?: (query: any) => void;
}

const SearchButton = ({ onSearch }: SearchButtonProps) => {
  const inputRef = useRef(null);
  const formRef = useRef(null);
  const [searchResults, setSearchResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const { searchPosts } = useSearch();
  const { t } = useTranslation(["blog"]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const searchResults = document.querySelector(".search-modal");
      const isClickInResults = searchResults?.contains(event.target as Node);
      const isClickInForm = formRef.current?.contains(event.target as Node);

      if (!isClickInForm && !isClickInResults) {
        if (inputRef.current) {
          inputRef.current.value = "";
          inputRef.current.blur();
        }
        setShowResults(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const value = inputRef.current?.value.trim() || "";
    if (value) {
      const results = searchPosts(value);
      setSearchResults(results);
      setShowResults(true);
      // Call the onSearch prop if provided
      if (onSearch) onSearch(value);
    }
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value.length >= 2) {
      const results = searchPosts(value);
      setSearchResults(results);
      setShowResults(true);
      // Call the onSearch prop if provided
      if (onSearch) onSearch(value);
    } else {
      setSearchResults([]);
      setShowResults(false);
    }
  };

  const handleResultClick = () => {
    if (inputRef.current) {
      inputRef.current.value = "";
    }
    setShowResults(false);
  };

  return (
    <>
      <form className='search' onSubmit={handleSubmit} ref={formRef}>
        <input
          ref={inputRef}
          type='text'
          placeholder={t("chrome.searchPlaceholder", "Search articles")}
          aria-label={t("chrome.searchPlaceholder", "Search articles")}
          onChange={handleChange}
        />
        <div>
          <svg>
            <use xlinkHref='#path' />
          </svg>
        </div>
      </form>

      {/* Render SearchResults using portal */}
      {ReactDOM.createPortal(
        <SearchResults
          results={searchResults}
          isVisible={showResults}
          onClose={handleResultClick}
        />,
        document.getElementById("root-search-results")
      )}

      <svg xmlns='http://www.w3.org/2000/svg' style={{ display: "none" }}>
        <symbol
          xmlns='http://www.w3.org/2000/svg'
          viewBox='0 0 160 28'
          id='path'
        >
          <path
            d='M32.9418651,-20.6880772 C37.9418651,-20.6880772 40.9418651,-16.6880772 40.9418651,-12.6880772 C40.9418651,-8.68807717 37.9418651,-4.68807717 32.9418651,-4.68807717 C27.9418651,-4.68807717 24.9418651,-8.68807717 24.9418651,-12.6880772 C24.9418651,-16.6880772 27.9418651,-20.6880772 32.9418651,-20.6880772 L32.9418651,-29.870624 C32.9418651,-30.3676803 33.3448089,-30.770624 33.8418651,-30.770624 C34.08056,-30.770624 34.3094785,-30.6758029 34.4782612,-30.5070201 L141.371843,76.386562'
            transform='translate(83.156854, 22.171573) rotate(-225.000000) translate(-83.156854, -22.171573)'
          />
        </symbol>
      </svg>
    </>
  );
};

export default SearchButton;
